import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
} from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";
import type { NodeVariable, Variable } from "../../types";
import { DCConditionOperationSelector } from "./condition-selector";
import type { LogicalOperator } from "../../if/types";
import { TypeValueInput } from "./ValueInput";
import type { DCOperation } from "../types";
import { useDataCenterNode } from "../context";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { VariableSelector } from "./VariableSelector";
import { isValidNumericRange } from "@/utils/format";

export interface DCConditionData {
  id: string;
  variable: Variable | null;
  operation: DCOperation | null;
  comparativeValue: string | Variable;
  initialRangeValue: string | Variable;
  endRangeValue: string | Variable;
  logicalOperator: LogicalOperator | null;
  operationValueType: "input" | "variable";
  initialRangeValueType: "input" | "variable";
  endRangeValueType: "input" | "variable";
}
export interface DCConditionProps {
  variables: Variable[];
  className?: string;
  buttonSize: "xs" | "default";
}
export const DCCondition = ({
  variables,
  className,
  buttonSize = "default",
}: DCConditionProps) => {
  const { conditions, setConditions, logicalOperator, setLogicalOperator } =
    useDataCenterNode();
  const { variables: inputVariables } = useGetVariables();

  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        id: new Date().getTime().toString(),
        variable: null,
        operation: null,
        comparativeValue: "",
        initialRangeValue: "",
        endRangeValue: "",
        logicalOperator: "and",
        operationValueType: "input",
        initialRangeValueType: "input",
        endRangeValueType: "input",
      },
    ]);
  };

  const validConditions = conditions.every(
    (condition) =>
      condition.variable &&
      condition.operation &&
      (!condition.operation.needsComparativeValue ||
        (condition.operation.needsComparativeValue &&
          condition.comparativeValue !== "")) &&
      (!condition.operation?.needsComparativeRange ||
        (condition.operation?.needsComparativeRange &&
          condition.initialRangeValue !== "" &&
          condition.endRangeValue !== "" &&
          isValidNumericRange(condition))),
  );

  const deleteCondition = (id: string) => {
    // Prevent deletion if only one condition remains
    if (conditions.length <= 1) return;
    setConditions(conditions.filter((cond) => cond.id !== id));
  };

  const updateCondition = (id: string, updates: Partial<DCConditionData>) => {
    if (updates.logicalOperator) {
      setLogicalOperator(updates.logicalOperator);
    }

    if (updates.operation) {
      setConditions(
        conditions.map((cond) =>
          cond.id === id
            ? {
                ...cond,
                ...updates,
                operationValueType: "input",
                initialRangeValueType: "input",
                endRangeValueType: "input",
                comparativeValue: "",
                endRangeValue: "",
                initialRangeValue: "",
              }
            : cond,
        ),
      );
      return;
    }

    setConditions(
      conditions.map((cond) =>
        cond.id === id ? { ...cond, ...updates } : cond,
      ),
    );
  };

  return (
    <div className={cn("w-full flex flex-col gap-1 p-6", className)}>
      {conditions.map((condition, index) => {
        console.log(conditions);
        return (
          <ConditionRow
            includeLabels={index === 0}
            includeLogicalOperator={index > 0}
            logicalOperator={logicalOperator}
            condition={condition}
            key={condition.id}
            variables={variables}
            removeCondition={deleteCondition}
            updateCondition={updateCondition}
            inputVariables={inputVariables}
            canDelete={conditions.length > 1}
            required={index === 0}
          />
        );
      })}

      <div>
        <Button
          type="button"
          variant="tertiary"
          size={buttonSize}
          disabled={!validConditions}
          onClick={addCondition}
        >
          Añadir condición
          <Icon type="far" name="plus" />
        </Button>
      </div>
    </div>
  );
};

const ConditionRow = ({
  includeLabels,
  includeLogicalOperator,
  logicalOperator,
  required,
  variables,
  condition,
  inputVariables,
  updateCondition,
  removeCondition,
  canDelete = true,
}: {
  includeLabels?: boolean;
  includeLogicalOperator?: boolean;
  logicalOperator: LogicalOperator | null;
  required?: boolean;
  variables: Variable[];
  condition: DCConditionData;
  inputVariables: NodeVariable[];
  updateCondition: (id: string, updates: Partial<DCConditionData>) => void;
  removeCondition: (id: string) => void;
  canDelete?: boolean;
}) => {
  const {
    id,
    variable,
    comparativeValue,
    operation,
    initialRangeValue,
    initialRangeValueType,
    endRangeValue,
    endRangeValueType,
    operationValueType,
  } = condition;

  const getRangeValidationMessage = (): string | undefined => {
    if (!operation?.needsComparativeRange) return undefined;
    if (!initialRangeValue || !endRangeValue) return undefined;

    if (
      initialRangeValueType === "variable" ||
      endRangeValueType === "variable"
    ) {
      return undefined;
    }
    const initialNum = parseFloat(initialRangeValue as string);
    const endNum = parseFloat(endRangeValue as string);
    if (isNaN(initialNum) || isNaN(endNum)) return undefined;
    if (endNum <= initialNum) {
      return "El valor final debe ser mayor que el valor inicial";
    }
    return undefined;
  };

  const rangeError = getRangeValidationMessage();

  return (
    <div
      className={cn(
        "w-full grid place-items-center gap-2",
        canDelete ? "grid-cols-[1fr_32px]" : "grid-cols-1",
      )}
    >
      <GroupTextField
        label={includeLabels ? "Condición" : ""}
        className="w-full"
        required={required}
        helperText={
          rangeError
            ? rangeError
            : includeLabels
              ? "Filtrar los registros según su metadata."
              : undefined
        }
        error={rangeError}
      >
        <GroupTextFieldRow>
          {includeLogicalOperator && (
            <Button
              variant="quiet"
              type="button"
              className="h-full text-body-loud capitalize"
              onClick={() => {
                let newOperator: LogicalOperator = "and";
                if (logicalOperator === "and") {
                  newOperator = "or";
                } else {
                  newOperator = "and";
                }
                updateCondition(id, { logicalOperator: newOperator });
              }}
            >
              {logicalOperator}
            </Button>
          )}
          <VariableSelector
            variables={[
              {
                name: "Ninguna",
                nodeId: "__none__",
                type: "data-center-node",
                variables: variables,
              },
            ]}
            selectedVariable={variable as Variable}
            placeholder="Selecciona una columna"
            onSelectVariable={(variable) => {
              const selectedVar = variables.find((v) => v.id === variable?.id);
              if (selectedVar) {
                updateCondition(id, {
                  variable: selectedVar,
                  operation: null,
                  comparativeValue: "",
                  operationValueType: "input",
                });
              }
            }}
            triggerGroup={
              includeLogicalOperator
                ? "middle-up"
                : operation?.needsComparativeValue ||
                    operation?.needsComparativeRange
                  ? "up-left"
                  : "left"
            }
          />
          <DCConditionOperationSelector
            selectedVariable={variable}
            selectedOperation={operation}
            onSelectOperation={(op) =>
              updateCondition(id, {
                operation: op,
                initialRangeValue: "",
                endRangeValue: "",
                initialRangeValueType: "input",
                endRangeValueType: "input",
              })
            }
            group={
              operation?.needsComparativeValue ||
              operation?.needsComparativeRange
                ? "up-right"
                : "right"
            }
            disabled={!variable}
          />
        </GroupTextFieldRow>
        {operation?.needsComparativeValue && (
          <GroupTextFieldRow>
            <Button
              variant="quiet"
              type="button"
              className="font-mono"
              onClick={() => {
                updateCondition(id, {
                  operationValueType:
                    operationValueType === "variable" ? "input" : "variable",
                  comparativeValue: undefined,
                });
              }}
            >
              {operationValueType === "variable" ? "val" : "inp"}
            </Button>
            <TypeValueInput
              inputType={operationValueType}
              valueType={operation.comparativeValueType ?? "string"}
              value={comparativeValue}
              onValueChange={(value) =>
                updateCondition(id, { comparativeValue: value })
              }
              inputVariables={inputVariables}
              filterByType={variable?.type}
            />
          </GroupTextFieldRow>
        )}
        {operation?.needsComparativeRange && (
          <GroupTextFieldRow>
            {/* Initial Range Value */}
            <Button
              variant="quiet"
              type="button"
              className="font-mono"
              onClick={() => {
                updateCondition(id, {
                  initialRangeValueType:
                    initialRangeValueType === "variable" ? "input" : "variable",
                  initialRangeValue: undefined,
                });
              }}
            >
              {initialRangeValueType === "variable" ? "val" : "inp"}
            </Button>
            <TypeValueInput
              inputType={initialRangeValueType}
              valueType={operation.comparativeValueType ?? "string"}
              value={initialRangeValue}
              onValueChange={(value) =>
                updateCondition(id, { initialRangeValue: value })
              }
              placeholder="Número inicial"
              showInputIcon={false}
              inputVariables={inputVariables}
              filterByType={variable?.type}
            />
            {/* End Range Value */}
            <Button
              variant="quiet"
              type="button"
              className="font-mono border-t-0"
              onClick={() => {
                updateCondition(id, {
                  endRangeValueType:
                    endRangeValueType === "variable" ? "input" : "variable",
                  endRangeValue: undefined,
                });
              }}
            >
              {endRangeValueType === "variable" ? "val" : "inp"}
            </Button>
            <TypeValueInput
              inputType={endRangeValueType}
              valueType={operation.comparativeValueType ?? "string"}
              value={endRangeValue}
              onValueChange={(value) =>
                updateCondition(id, { endRangeValue: value })
              }
              placeholder="Número final"
              showInputIcon={false}
              inputVariables={inputVariables}
              filterByType={variable?.type}
            />
          </GroupTextFieldRow>
        )}
      </GroupTextField>

      {canDelete && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Eliminar condición"
          className="size-7"
          type="button"
          onClick={() => removeCondition(id)}
        >
          <Icon type="far" name="times" />
        </Button>
      )}
    </div>
  );
};
